import { description, firmware_needed } from "translations-en";

export const en = {
    "app_name": "Remote Access",
    "alert_default_text": "An Error occured!",
    "alert_default_success": "Success!",
    description,
    "no_service_worker": "Your browser needs support for ServiceWorkers to be able to run the Remote Access.",
    "banner": {
        "title": "Upcoming Maintenance!",
        "message": "Maintenance is scheduled for Monday 01.12.2025 from 10:00 A.M. to 11:30 P.M. During this time, Remote Access will be unavailable."
    },
    "not_now": "Not now",
    "allow": "Show browser prompt",
    "storage_persistence": {
        "title": "Keep you signed in",
        "body": "We use your browser's storage to keep you signed in. Some browsers clear this data automatically (e.g., on low storage or after some time). Allowing persistent storage helps prevent unexpected logouts. After you tap the 'Show browser prompt' button, your browser will show a permission prompt so you can confirm.",
        "remember_choice": "Don't ask again"
    },
    "user": {
        "account_actions": "Account actions",
        "profile_information": "Account information",
        "save_changes": "Save changes",
        "user_id": "Account-ID",
        "email": "Email-address",
        "name": "Name",
        "change": "Change",
        "change_password": "Change password",
        "change_password_button": "Change password",
        "current_password": "Current password",
        "current_password_error_message": "Must not be empty.",
        "new_password": "New password",
        "new_password_error_message": "Must be at least 8 characters long.",
        "confirm_new_password": "Confirm new password",
        "confirm_new_password_error_message": "Passwords do not match",
        "close": "Close",
        "delete_user": "Delete account",
        "delete_user_button": "Delete account",
        "password": "Password",
        "password_invalid": "Password is wrong",
        "invalid_name": "The name must not be empty",
        "logout_all": "Logout from all sessions",
        "get_user_failed": "Loading account data failed with status code {{status}}: {{response}}. Please reload the page.",
        "update_user_failed": "Updating the account-data failed with status code {{status}}: {{response}}. Please reload the page.",
        "update_password_failed": "Updating the password failed with status code {{status}}: {{response}}. Please reload the page.",
        "delete_user_failed": "Deleting account failed with status code {{status}}: {{response}}. Please reload the page.",
        "email_change_disabled": "Email changes are disabled because you have chargers with old firmware versions. Please update your chargers first.",
        "local_settings": "Local settings",
        "debug_mode": "Debug mode",
        "save_recovery_file": "Save recovery file",
        "save_recovery_file_password": "Enter your password to download the recovery file.",
        "save_recovery_file_failed": "Failed to download recovery file with status code {{status}}: {{response}}",
        "save_recovery_file_wrong_password": "Wrong password. Could not decrypt secret.",
        "download": "Download",
    },
    "recovery": {
        "recovery": "Password recovery",
        "new_password": "New password",
        "confirm_password": "Confirm password",
        "confirm_password_error_message": "Passwords do not match",
        "recovery_file": "Recovery File",
        "submit": "Submit",
        "invalid_file": "File is invalid",
        "token_expired": "The token has expired. Please start the process again.",
        "no_file_warning_heading": "Are you sure?",
        "no_file_warning_body": "You did not provide a recovery file. If you continue, we will generate a new device secret. This will cause you to lose access to all devices that are currently linked to your account and you will have to re-add them manually. If you still have access to your recovery file, we strongly recommend using it instead.",
        "no_file_warning_cancel": "Cancel",
        "no_file_warning_proceed": "Continue without file",
        "no_file_warning_ack": "I understand that continuing without a recovery file will disconnect my devices and require re-adding them."
    },
    "chargers": {
        "loading": "Loading...",
        "charger_name": "Name",
        "charger_id": "Device-ID",
        "mobile_charger_id": "UID",
        "status": "Status",
        "status_connected": "Connected",
        "status_disconnected": "Disconnected",
        "connect": "Connect",
        "remove": "Remove",
        "close": "Close",
        "loading_secret_failed": "Loading secret failed with status code {{status}}: {{response}}. Please reload the page.",
        "loading_keys_failed": "Loading connection keys failed with status code {{status}}: {{response}}. Please reload the page.",
        "all_keys_in_use": "Currently all remote connections are in use",
        "no_keys": "You need to register this device again.",
        "loading_devices_failed": "Loading devices failed with status code {{status}}: {{response}}. Please reload the page.",
        "no_devices": "No devices registered yet. Please connect your device to this account to get started.",
        "connect_error_text": "Connecting to device {{charger_id}} failed with status code {{status}}: {{response}}. Please reload the page.",
        "remove_error_text": "Removing device {{charger_id}} failed with status code {{status}}: {{response}}. Please reload the page.",
        "delete_modal_heading": "Remove device {{name}}",
        "delete_modal_body": "Are you sure you want to remove device {{name}}? This is permanent and the device can only be restored if you have direct access to the web interface.",
        "select_sorting": "Sort",
        "sorting_sequence_asc": "Ascending",
        "sorting_sequence_desc": "Descending",
        "note": "Note",
        "firmware_version": "Firmware Version",
        "last_state_change": "State Change",
        "added_at": "Added to your account",
        "time_just_now": "Just now",
        "time_minutes_ago": "{{count}}m ago",
        "time_hours_ago": "{{count}}h ago",
        "time_days_ago": "{{count}}d ago",
        "edit_note_heading": "Edit note",
        "edit_note_failed": "Editing note failed, please reload the page.",
        "accept": "Accept",
        "decline": "Decline",
        "show_more": "Show more",
        "show_less": "Show less",
        "abort": "Cancel",
        "not_connected": "The device is currently not reachable. If the problem persists, please check your internet connection and the device's connection to the internet.",
        "connecting": "Connecting",
        "loading_webinterface": "Loading webinterface",
        "invalid_key": "The keys saved on the device seem to be corrupted. If the error persists, remove the device and add it again.",
        "websocket_parse_error": "Failed to parse device update from server.",
        "connection_timeout": "Timeout",
        "connection_timeout_text": "A timeout occured while establishing the connection. Please try again later or contact us in case the problem persists.",
        "search_devices": "Search devices",
        "search_devices_placeholder": "Search by name, ID, note, or status...",
        "clear_search": "Clear search",
        "no_devices_found": "No devices found matching your search.",
        "local": "Local",
        "connect_locally": "Connect locally",
        "connect_via_cloud": "Connect via remote-access",
        "manage_groupings": "Manage Groups",
        "groupings": "Groups",
        "create_grouping": "Create Group",
        "edit_grouping": "Edit Group",
        "delete_grouping": "Delete Group",
        "grouping_name": "Group Name",
        "grouping_name_placeholder": "Enter group name...",
        "grouping_devices": "Devices in Group",
        "add_devices_to_grouping": "Add Devices to Group",
        "remove_device_from_grouping": "Remove from Group",
        "no_groupings": "No groups created yet",
        "no_groupings_found": "No groups found matching your search.",
        "create_grouping_success": "Group created successfully",
        "update_grouping_success": "Group updated successfully",
        "delete_grouping_success": "Group deleted successfully",
        "create_grouping_failed": "Failed to create group: {{error}}",
        "update_grouping_failed": "Failed to update group: {{error}}",
        "delete_grouping_failed": "Failed to delete group: {{error}}",
        "add_device_to_grouping_failed": "Failed to add device to group: {{error}}",
        "remove_device_from_grouping_failed": "Failed to remove device from group: {{error}}",
        "load_groupings_failed": "Failed to load groups: {{error}}",
        "delete_grouping_confirm": "Are you sure you want to delete the group \"{{name}}\"?",
        "all_devices": "All Devices",
        "filter_by_grouping": "Filter by Group",
        "grouping_empty": "This group has no devices",
        "select_devices": "Select devices to add",
        "search_groupings": "Search",
        "save": "Save",
        "cancel": "Cancel",
        "set_as_default": "Set as default",
        "default_grouping": "Default",
        "set_as_default_replaces": "This will replace \"{{name}}\" as the default group.",
        "set_default_failed": "Failed to set default group: {{error}}",
        "no_group": "Ungrouped",
        "local_devices": "Local devices",
        "group_by_toggle": "Group devices by group",
        "group_by_on": "Grouped",
        "group_by_off": "Flat"
    },
    "navbar": {
        "home": "Home",
        "chargers": "Devices",
        "logout": "Logout",
        "token": "Token",
        "close": "Close"
    },
    "register": {
        "register": "Register",
        "name": "Name",
        "name_error_message": "The name must not be empty",
        "email": "Email-address",
        "email_error_message": "The email-address must not be empty",
        "password": "Password",
        "password_error_message": "Must be at least 8 characters long and contain at least one lowercase and uppercase letter, and one special character.",
        "confirm_password": "Confirm password",
        "confirm_password_error_message": "Passwords do not match",
        "accept_privacy_notice": "I have read, understood and I am accepting the <0>privacy notice</0>.",
        "accept_terms_and_conditions": "I have read, understood and I am accepting the <0>terms and conditions</0>.",
        "save_recovery_data": "Save recovery file",
        "save": "Save",
        "save_recovery_data_text": "Since the access data for the devices can only be decrypted with the correct password, you need this file to restore access to your devices if you forget your password. Keep this file safe and inaccessible to others, as it is equivalent to your password.",
        "save_recovery_data_confirmation": "I have downloaded and safely stored the recovery file",
        "close": "Close",
        "registration_successful": "Registration was successful. You should receive an email with a confirmation link within the next few minutes.",
        "email_already_exists": "An account with this email address already exists."
    ,"resend_verification": "Resend verification email"
    ,"resend_success": "Verification email sent (if the address is not yet verified)."
    ,"resend_error": "Could not resend verification email. Please try again later."
    },
    "login": {
        "password_recovery": "Password reset",
        "email":"Email-address",
        "send": "Send",
        "close": "Close",
        "password": "Password",
        "login": "Login",
        "wrong_credentials": "Email-address or password wrong.",
        "success_alert_text": "You should receive an email within the next few minutes.",
        "success_alert_heading": "Success",
        "error_alert_text": "Failed to start recovery with status {{status}}: {{text}}",
        "verify_before_login": "Please verify your email address before logging in",
        "verify_before_login_heading": "Email not verified",
        "verify_success": "Your account has been verified. You can now log in.",
        "verify_success_heading": "Account activated"
    },
    "password_strength": {
        "strength": "Strength",
        "entropy": "Entropy",
        "bits": "bits",
        "very_weak": "Very Weak",
        "weak": "Weak",
        "fair": "Fair",
        "strong": "Strong",
        "very_strong": "Very Strong"
    },
    "footer": {
        "imprint": "Imprint",
        "terms_of_use": "Terms and Conditions",
        "privacy_notice": "Privacy Notice",
        "mobile_app_hint": "Also available as a mobile app"
    },
    "mobile_app": {
        "hint_title": "Get the mobile app",
        "hint_text": "For a better experience on mobile devices, download our native app.",
        "app_store": "App Store",
        "play_store": "Google Play"
    },
    "app": {
        "close_remote_access": "Close Remote-Access"
    },
    "tokens": {
        "fetch_user_failed": "Account could not be retrieved. Please reload the page.",
        "fetch_tokens_failed": "Tokens could not be retrieved. Please reload the page.",
        "unexpected_error": "An unexpected error occurred. Please reload the page.",
        "create_token_failed": "Token could not be created. Please reload the page.",
        "delete_token_failed": "Token could not be deleted. Please reload the page.",
        "info_heading": "What authorization tokens do",
        "info_body": "Authorization tokens let you add devices without sharing your password. Single-use tokens expire right after their first use, while reusable tokens stay active until you delete them. \n\n Alternatively, you can also log in to the device's web interface using your email address and password to connect it to your account.",
        "copy_success": "Success",
        "copy_success_text": "Token copied to clipboard",
        "copy_failed": "Token could not be copied",
        "add_device": "Add device",
        "create_token": "Create authorization token",
        "use_once": "Use once",
        "name": "Name",
        "name_placeholder": "Name",
        "created": "Created",
        "used": "Used",
        "last_used": "Last used",
        "never_used": "Never",
        "create": "Create token",
        "existing_tokens": "Existing tokens",
        "reusable": "Reusable",
        "copy": "Copy",
        "delete": "Delete",
        "single_use_description": "This token can only be used once and will automatically expire after first use",
        "multi_use_description": "This token can be used multiple times until manually deleted",
        "sort_label": "Sort tokens",
        "sort_created_desc": "Newest first",
        "sort_created_asc": "Oldest first",
        "sort_name_asc": "Name A-Z",
        "sort_name_desc": "Name Z-A",
        "sort_last_used_desc": "Recently used",
        "search_label": "Search tokens",
        "search_placeholder": "Search by name",
        "no_tokens": "No authorization tokens yet. Create one above to get started.",
        "no_tokens_search": "No tokens match your search.",
        firmware_needed,
        "decrypt_name_failed": "Failed to decrypt token name."
    },
    "wg_client": {
        "not_initialized": "The WireGuard client is not initialized. Please reload the page."
    },
    "version_checker": {
        "new_version_available": "A new version of the application is available. Reload now to get the latest features and bug fixes?",
        "already_latest": "You are already using the latest version.",
        "new_version_confirm": "A new version is available. Reload now?"
    },
    "iframe": {
        "error_message": "Remote Access cannot run in an iframe"
    },
    "android_smart_banner": {
        "close": "Close",
        "text": "Get the app",
        "view": "Show"
    },
    "provisioning": {
        "title": "Provision device",
        "provisioning_hint": "Have a new device? Scan its QR code to add it to your account.",
        "provisioning_button": "Add via QR code"
    },
    "local_devices": {
        "title": "Local network",
        "searching": "Searching for devices on the local network...",
        "none_found": "No devices found on the local network.",
        "connect": "Connect",
        "firmware": "Firmware"
    }
};
